/**
 * HouseholdProduct (household-owned product wrapper).
 *
 * Ported from legacy `frontend/src/types.ts`. Shape matches the backend's
 * `GET /api/householdproducts` response exactly — validator will catch drift.
 */

import { z } from 'zod';
import {
  pgNumeric,
  priceCurrencySchema,
  productAvailabilitySchema,
} from './primitives';

// Wire shape: backend serializers emit snake_case (per the Postgres column
// naming) and the Zod schema mirrors that wire shape verbatim. T12.0.18.1.1
// Step 2.5 briefly collapsed within-chunk underscores on the client side; the
// Step 3 follow-up reverted that collapse so both sides converge on
// snake_case-with-new-term again (mirroring `RetailerOffer.sale_description`).
export const myProductSchema = z.object({
  id: z.string(),
  household_id: z.string(),

  // Display / Identity
  display_name: z.string(),
  category_id: z.string().nullable().optional(),
  notes: z.string().nullable().optional(),
  tags: z.array(z.string()),
  image_url: z.string().nullable().optional(),

  // Quantity / Packaging Preferences
  //
  // T36.3 / ADR-015 — the legacy `preferred_unit_type` + `preferred_package_size_*`
  // columns are retired: the resolved measurement snapshot below is the single
  // measurement source. The fields no longer appear on the wire (a legacy client
  // that still emits them is silently stripped by the schema's default `.strip()`).

  // T36.3 / ADR-015 §6 — resolved measurement snapshot. The HouseholdProduct
  // stores its chosen packing's count/value/unit/dimension + the dimension's
  // display `forms` (so weight/volume unit-switching renders OFFLINE — SystemProduct
  // packings are NOT synced). NULL across the set when the product has no linked
  // SystemProduct / no offer-sourced packing (no fabricated unit, no `ea`).
  // `provenance` = `system` (resolved from the link, refreshed on (re)link) or
  // `user` (a picker override, immutable to automation). Optional so older clients
  // and pre-column responses still parse; the formatter treats absence as "no
  // resolved measurement".
  measurement_pack_count: z.number().int().nullable().optional(),
  measurement_value: pgNumeric.nullable().optional(),
  measurement_unit: z.string().nullable().optional(),
  measurement_dimension: z.string().nullable().optional(),
  measurement_forms: z.record(z.string(), z.number()).nullable().optional(),
  measurement_provenance: z
    .union([z.literal('system'), z.literal('user')])
    .nullable()
    .optional(),
  // The packing that supplied the snapshot. The settings "Pick your pack" list
  // keys its selected highlight on this so the highlight survives a display-unit
  // switch (the switch keeps the packing, only re-points the unit). Optional so
  // older clients / pre-column responses still parse.
  source_packing_id: z.string().nullable().optional(),
  default_quantity: pgNumeric,
  // T12.1.7 / ADR-006 — cadence nudge. NULL ⇒ algorithm-derives the cadence;
  // an integer pins "every N days". Optional so the wire shape aligns with
  // older clients before the column landed.
  cadence_days: z.number().int().nullable().optional(),

  // Shopping Preferences (T12.0.1.4 — preferred/fallback arrays replaced by
  // the `householdproducts_retailers_preferences` junction; populated by JOIN
  // server-side. Per-row sync via `product_retailer_pref_added` /
  // `product_retailer_pref_removed`). Always an array; empty if none.
  retailer_preferences: z.array(
    z.object({
      householdproduct_id: z.string(),
      retailer_id: z.string(),
      created_at: z.string(),
    })
  ),
  allow_substitutions: z.boolean(),
  substitution_notes: z.string().nullable().optional(),
  always_keep_stocked: z.boolean(),

  // Expected Price (household expectation)
  // Postgres NUMERIC arrives as a string over the wire (e.g. "4.99"). The
  // shared `pgNumeric` schema (`z.coerce.number()`) accepts both numbers and
  // numeric strings and produces a `number`. See TD#1 in T8.4.
  expected_price_value: pgNumeric.nullable().optional(),
  expected_price_currency: priceCurrencySchema,
  expected_price_notes: z.string().nullable().optional(),

  // Seed Metadata. Backend emits `seed_householdproduct_id` (T12.0.1.3 rename of
  // the prior `seed_product_id`; the legacy `platform_catalog_product_id`
  // alias was dropped in the same Task).
  seed_householdproduct_id: z.string().nullable().optional(),
  seeded_at: z.string().nullable().optional(),

  // Audit
  created_by: z.string().nullable().optional(),
  created_at: z.string(),
  updated_at: z.string(),
});
export type HouseholdProduct = z.infer<typeof myProductSchema>;

export const myProductWithDetailsSchema = myProductSchema.extend({
  category_name: z.string().nullable().optional(),
  linked_offer_count: pgNumeric,
  favorite_offer_count: pgNumeric,
  default_offer_id: z.string().nullable().optional(),
  default_offer_title: z.string().nullable().optional(),
  default_offer_price: pgNumeric.nullable().optional(),
  default_store_name: z.string().nullable().optional(),
  // T12.5.1 (F) — the SystemProduct id(s) this product is linked to. Lets the
  // Deals surface light ★ On your list on every offer/deal for a product on the
  // list (identity match), not just the one offer the item selected. Optional so
  // older clients and pre-column responses still parse; the reader defaults to []
  // (the backend always emits the array).
  linked_systemproduct_ids: z.array(z.string()).optional(),
});
export type HouseholdProductWithDetails = z.infer<typeof myProductWithDetailsSchema>;

export const householdProductsResponseSchema = z.object({
  products: z.array(myProductWithDetailsSchema),
  total: z.number(),
});
export type HouseholdProductsResponse = z.infer<typeof householdProductsResponseSchema>;

// ---------------------------------------------------------------------------
// HouseholdProduct preferences surface (T12.1.7) — the soft, advisory signals
// the suggestion algorithm (T12.5) weighs. Never hard filters. Wire shape
// mirrors `GET /api/householdproducts/:id/preferences` (snake_case).
// ---------------------------------------------------------------------------

// T12.1.7.1 — `exclude` ("Don't suggest") is the third value: a soft downweight
// the suggestion algorithm (T12.5) weighs, never a hard filter here.
export const brandPreferenceSchema = z.union([
  z.literal('preferred'),
  z.literal('substitute'),
  z.literal('exclude'),
]);
export type BrandPreference = z.infer<typeof brandPreferenceSchema>;

export const householdProductPreferencesSchema = z.object({
  brands: z.array(
    z.object({
      brand_id: z.string(),
      preference: brandPreferenceSchema,
      displayorder: z.number(),
    })
  ),
  retailers: z.array(z.object({ retailer_id: z.string() })),
  storeLocations: z.array(
    z.object({
      storelocation_id: z.string(),
      displayorder: z.number(),
    })
  ),
  // T12.1.8.2 — the household's SystemProduct connection (the primary,
  // foreground product-preference signal). Same shape as `brands`: a
  // preferred/substitute preference per pinned product line, ordered by displayorder.
  // T55.6 — `category_label` added (optional, read-only): strongest Category tag name
  // from the SystemProduct's highest-weight entry in SystemProductCategory. Null/undefined
  // = no Category tag, falls into the client-side "Other" bucket on grouping.
  systemProducts: z.array(
    z.object({
      systemproduct_id: z.string(),
      name: z.string(),
      image_url: z.string().nullable().optional(),
      preference: brandPreferenceSchema,
      displayorder: z.number(),
      category_label: z.string().nullable().optional(),
    })
  ),
  // T27.2.7.10 — the AUTO-MATCHED (provenance=system) SystemProduct links the
  // auto-matcher created for this product. The fine-tune Auto-matched block
  // renders these with Prefer / Avoid; either action flips the link to a
  // deliberate user stance (it leaves this set). `brand_label` is the row's meta
  // line. Optional + defaulted so older clients and pre-field responses still
  // parse; readers treat absence as an empty set.
  // T55.6 — `category_label` added (optional, read-only): strongest Category tag name,
  // same as systemProducts.
  autoMatched: z
    .array(
      z.object({
        systemproduct_id: z.string(),
        name: z.string(),
        image_url: z.string().nullable().optional(),
        brand_label: z.string().nullable().optional(),
        category_label: z.string().nullable().optional(),
      })
    )
    .optional(),
  cadencedays: z.number().int().nullable(),
});
export type HouseholdProductPreferences = z.infer<typeof householdProductPreferencesSchema>;

// ---------------------------------------------------------------------------
// T36.3 — measurement settings picker. The valid packing/unit choices for a
// HouseholdProduct's linked SystemProducts. `GET
// /api/householdproducts/:id/measurement-choices` returns `{ choices: [...] }`.
// ---------------------------------------------------------------------------

export const measurementChoiceSchema = z.object({
  packing_id: z.string(),
  systemproduct_id: z.string(),
  pack_count: z.number().int(),
  value: pgNumeric.nullable(),
  unit: z.string().nullable(),
  dimension: z.string(),
  forms: z.record(z.string(), z.number()),
});
export type MeasurementChoice = z.infer<typeof measurementChoiceSchema>;

export const measurementChoicesResponseSchema = z.object({
  choices: z.array(measurementChoiceSchema),
});
export type MeasurementChoicesResponse = z.infer<typeof measurementChoicesResponseSchema>;

// Managed Brand catalog row — `GET /api/brands` returns `{ brands: [...] }`.
// Only the picker-relevant fields are modelled; the catalog carries more.
export const brandSchema = z.object({
  id: z.string(),
  name: z.string(),
  normalized_name: z.string(),
});
export type Brand = z.infer<typeof brandSchema>;

export const brandsResponseSchema = z.object({
  brands: z.array(brandSchema),
});
export type BrandsResponse = z.infer<typeof brandsResponseSchema>;

// ---------------------------------------------------------------------------
// HouseholdProduct_RetailerOffer_Link
// ---------------------------------------------------------------------------

export const myProductOfferLinkSchema = z.object({
  id: z.string(),
  householdproduct_id: z.string(),
  retaileroffer_id: z.string(),
  is_favorite: z.boolean(),
  is_default: z.boolean(),
  notes: z.string().nullable().optional(),
  created_at: z.string(),
  updated_at: z.string(),
});
export type HouseholdProduct_RetailerOffer_Link = z.infer<typeof myProductOfferLinkSchema>;

export const myProductOfferLinkWithDetailsSchema = myProductOfferLinkSchema.extend({
  offer_title: z.string(),
  offer_brand: z.string().nullable().optional(),
  offer_price: pgNumeric.nullable().optional(),
  store_name: z.string(),
  // T12.0.1.6 — `availability` dropped from `retaileroffers` (catalog rows have
  // no availability column post-split); `listProductOffers` no longer selects
  // it. Optional so the wire shape aligns with what the server actually emits.
  availability: productAvailabilitySchema.optional(),
});
export type HouseholdProduct_RetailerOffer_LinkWithDetails = z.infer<
  typeof myProductOfferLinkWithDetailsSchema
>;

export const myProductOffersResponseSchema = z.object({
  offers: z.array(myProductOfferLinkWithDetailsSchema),
});
export type HouseholdProductOffersResponse = z.infer<typeof myProductOffersResponseSchema>;
